import { useEffect, useMemo, useState } from 'react';
import { tradingService } from '../../services/TradingService';
import type { LatestCloseValue, MarketAssetClass } from '../../types/trading';

type CacheKey = `${MarketAssetClass}:${string}`;
type LatestCloseBySymbol = Record<string, LatestCloseValue | undefined>;

interface CachedLatestCloseEntry {
  value: LatestCloseValue;
  fetchedAtMs: number;
}

interface CachedMarketState {
  isOpen: boolean;
  fetchedAtMs: number;
}

const LATEST_CLOSE_TTL_MS = 60_000;
const latestCloseCache = new Map<CacheKey, CachedLatestCloseEntry>();
const inFlightByBatchKey = new Map<string, Promise<void>>();
let marketStateCache: CachedMarketState | null = null;

const normalizeSymbol = (symbol: string): string => symbol.trim().toUpperCase();

const toCacheKey = (assetClass: MarketAssetClass, symbol: string): CacheKey => {
  return `${assetClass}:${symbol}`;
};

const unavailableValue = (reason: string): LatestCloseValue => ({
  latest_close: null,
  latest_close_date: null,
  status: 'unavailable',
  reason,
});

const isFresh = (fetchedAtMs: number, nowMs: number): boolean => {
  return nowMs - fetchedAtMs <= LATEST_CLOSE_TTL_MS;
};

const readFromCache = (
  symbols: string[],
  assetClass: MarketAssetClass,
  nowMs: number,
): LatestCloseBySymbol => {
  const values: LatestCloseBySymbol = {};
  symbols.forEach((symbol) => {
    const cached = latestCloseCache.get(toCacheKey(assetClass, symbol));
    if (cached && isFresh(cached.fetchedAtMs, nowMs)) {
      values[symbol] = cached.value;
    }
  });
  return values;
};

const hasStaleOrMissingSymbols = (
  symbols: string[],
  assetClass: MarketAssetClass,
  nowMs: number,
): boolean => {
  for (const symbol of symbols) {
    const cached = latestCloseCache.get(toCacheKey(assetClass, symbol));
    if (!cached || !isFresh(cached.fetchedAtMs, nowMs)) {
      return true;
    }
  }
  return false;
};

const marketStateFromCache = (nowMs: number): boolean | null => {
  if (!marketStateCache) return null;
  return isFresh(marketStateCache.fetchedAtMs, nowMs) ? marketStateCache.isOpen : null;
};

export const __resetLatestCloseCacheForTests = () => {
  latestCloseCache.clear();
  inFlightByBatchKey.clear();
  marketStateCache = null;
};

export const useLatestClose = (
  symbols: string[],
  assetClass: MarketAssetClass = 'stock',
): { latestCloseBySymbol: LatestCloseBySymbol; marketIsOpen: boolean | null } => {
  const normalizedSymbols = useMemo(
    () => Array.from(new Set(symbols.map(normalizeSymbol).filter((symbol) => symbol.length > 0))).sort(),
    [symbols],
  );
  const symbolsKey = normalizedSymbols.join('|');
  const [refreshTick, setRefreshTick] = useState(0);
  const [latestCloseBySymbol, setLatestCloseBySymbol] = useState<LatestCloseBySymbol>(() =>
    readFromCache(normalizedSymbols, assetClass, Date.now()),
  );
  const [marketIsOpen, setMarketIsOpen] = useState<boolean | null>(() =>
    marketStateFromCache(Date.now()),
  );

  useEffect(() => {
    const timerId = window.setInterval(() => {
      setRefreshTick((prev) => prev + 1);
    }, LATEST_CLOSE_TTL_MS);
    return () => {
      window.clearInterval(timerId);
    };
  }, []);

  useEffect(() => {
    let canceled = false;
    const nowMs = Date.now();

    const refreshFromCache = () => {
      if (canceled) return;
      setLatestCloseBySymbol(readFromCache(normalizedSymbols, assetClass, Date.now()));
      setMarketIsOpen(marketStateFromCache(Date.now()));
    };

    refreshFromCache();

    if (normalizedSymbols.length === 0) {
      return () => {
        canceled = true;
      };
    }

    const needsSymbolFetch = hasStaleOrMissingSymbols(normalizedSymbols, assetClass, nowMs);
    const needsMarketFetch = marketStateFromCache(nowMs) == null;
    if (!needsSymbolFetch && !needsMarketFetch) {
      return () => {
        canceled = true;
      };
    }

    const batchKey = `${assetClass}:${normalizedSymbols.join(',')}`;
    let request = inFlightByBatchKey.get(batchKey);
    if (!request) {
      request = tradingService
        .getLatestClose(assetClass, normalizedSymbols)
        .then((response) => {
          const fetchedAtMs = Date.now();
          marketStateCache = {
            isOpen: response.market_is_open,
            fetchedAtMs,
          };
          normalizedSymbols.forEach((symbol) => {
            latestCloseCache.set(toCacheKey(assetClass, symbol), {
              value: response.values[symbol] ?? unavailableValue('latest close unavailable'),
              fetchedAtMs,
            });
          });
        })
        .catch(() => {
          const fetchedAtMs = Date.now();
          normalizedSymbols.forEach((symbol) => {
            latestCloseCache.set(toCacheKey(assetClass, symbol), {
              value: unavailableValue('failed to fetch latest close'),
              fetchedAtMs,
            });
          });
        })
        .finally(() => {
          inFlightByBatchKey.delete(batchKey);
        });
      inFlightByBatchKey.set(batchKey, request);
    }

    void request.then(() => {
      refreshFromCache();
    });

    return () => {
      canceled = true;
    };
  }, [assetClass, symbolsKey, normalizedSymbols, refreshTick]);

  return { latestCloseBySymbol, marketIsOpen };
};
